import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  FileDown,
  Info,
  Loader2,
  RefreshCw,
  Shield,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react';

import {
  getCompliancePosture,
  triggerComplianceSync,
  openHipaaReport,
  type CompliancePostureResponse,
  type ComplianceGap,
  type ComplianceSeverity,
} from '../../services/api';

// ----------------------------------------------------------------------------
// Presentation helpers
// ----------------------------------------------------------------------------

const SEVERITY_ORDER: Record<ComplianceSeverity, number> = {
  'must-fix': 0,
  'should-fix': 1,
  'nice-to-have': 2,
};

const SEVERITY_STYLES: Record<ComplianceSeverity, { label: string; badge: string; iconWrap: string }> = {
  'must-fix': {
    label: 'Must fix',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    iconWrap: 'text-red-600 dark:text-red-400',
  },
  'should-fix': {
    label: 'Should fix',
    badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    iconWrap: 'text-yellow-600 dark:text-yellow-400',
  },
  'nice-to-have': {
    label: 'Nice to have',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    iconWrap: 'text-blue-600 dark:text-blue-400',
  },
};

function scoreColor(score: number): { text: string; ring: string } {
  if (score >= 90) return { text: 'text-green-600 dark:text-green-400', ring: 'ring-green-500/40' };
  if (score >= 70) return { text: 'text-yellow-600 dark:text-yellow-400', ring: 'ring-yellow-500/40' };
  return { text: 'text-red-600 dark:text-red-400', ring: 'ring-red-500/40' };
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function stalenessBadge(s: CompliancePostureResponse['staleness']): { label: string; color: string } {
  switch (s) {
    case 'fresh':      return { label: 'Fresh',       color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    case 'stale':      return { label: 'Stale',       color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    case 'very_stale': return { label: 'Very stale',  color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
    case 'never_synced': return { label: 'Never synced', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
  }
}

// ----------------------------------------------------------------------------
// Score badge — big circle with the number
// ----------------------------------------------------------------------------

function ScoreBadge({ score }: { score: number }) {
  const { text, ring } = scoreColor(score);
  return (
    <div className={`flex flex-col items-center justify-center h-24 w-24 rounded-full ring-4 ${ring} bg-card border border-border`}>
      <span className={`text-3xl font-bold ${text}`}>{score}</span>
      <span className="text-xs text-muted-foreground -mt-1">/ 100</span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Gap card — one row per finding
// ----------------------------------------------------------------------------

function GapCard({ gap }: { gap: ComplianceGap }) {
  const style = SEVERITY_STYLES[gap.severity];
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-border rounded-lg bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-accent/30 transition-colors rounded-lg"
      >
        <div className={`mt-0.5 ${style.iconWrap}`}>
          {gap.severity === 'must-fix' ? <ShieldAlert className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${style.badge}`}>{style.label}</span>
            <span className="text-sm font-mono text-muted-foreground">{gap.id}</span>
          </div>
          <div className="mt-1.5 text-sm font-medium text-foreground">{gap.title}</div>
          {!expanded && (
            <div className="mt-1 text-xs text-muted-foreground line-clamp-1">{gap.detail}</div>
          )}
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground mt-1" /> : <ChevronRight className="h-4 w-4 text-muted-foreground mt-1" />}
      </button>
      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">What's wrong</div>
            <div className="text-foreground">{gap.detail}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Remediation</div>
            <div className="text-foreground">{gap.remediation}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------

export default function CompliancePage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [data, setData] = useState<CompliancePostureResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPassed, setShowPassed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getCompliancePosture(clientId, 'hipaa');
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load compliance posture');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const refresh = useCallback(async () => {
    if (!clientId || refreshing) return;
    setRefreshing(true);
    setRefreshMessage('Collecting fresh compliance data from the tenant — this takes 1-2 minutes…');
    try {
      const result = await triggerComplianceSync(clientId);
      // Completed inside the proxy window (fast path)
      if (result.success) {
        setRefreshMessage(`Refreshed in ${Math.round(result.durationMs / 1000)}s`);
      } else {
        setRefreshMessage(`Refresh finished with an issue: ${result.error ?? 'unknown error'}`);
      }
      await load();
      setRefreshing(false);
      setTimeout(() => setRefreshMessage(null), 8000);
    } catch {
      // The SWA proxy times out at 45s but the runbook keeps running server-side
      // and writes to Cosmos when it finishes (~90s). Wait it out, then reload
      // the cached data. A thrown error here is almost always that timeout, not
      // a real failure.
      setRefreshMessage('Still collecting in the background (up to ~2 min). The page will update automatically…');
      setTimeout(async () => {
        await load();
        setRefreshing(false);
        setRefreshMessage('Updated with the latest collected data.');
        setTimeout(() => setRefreshMessage(null), 8000);
      }, 75000);
    }
  }, [clientId, refreshing, load]);

  const generateReport = useCallback(async () => {
    if (!clientId || generatingReport) return;
    setGeneratingReport(true);
    setReportError(null);
    try {
      await openHipaaReport(clientId);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : 'Failed to generate report');
      setTimeout(() => setReportError(null), 8000);
    } finally {
      setGeneratingReport(false);
    }
  }, [clientId, generatingReport]);

  useEffect(() => { load(); }, [load]);

  const sortedGaps = useMemo(() => {
    if (!data?.evaluation) return [];
    return [...data.evaluation.gaps].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }, [data]);

  const gapCounts = useMemo(() => {
    const counts: Record<ComplianceSeverity, number> = { 'must-fix': 0, 'should-fix': 0, 'nice-to-have': 0 };
    for (const g of sortedGaps) counts[g.severity]++;
    return counts;
  }, [sortedGaps]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading compliance posture…
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 flex items-center gap-3">
        <AlertCircle className="h-5 w-5" />
        <div>{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const staleness = stalenessBadge(data.staleness);
  const evaluation = data.evaluation;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/clients/${clientId}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {data.clientName}
        </Link>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Shield className="h-8 w-8 text-primary-500" />
            <div>
              <h1 className="text-2xl font-semibold text-foreground">HIPAA Compliance</h1>
              <div className="text-sm text-muted-foreground">{data.clientName}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-accent text-sm"
              title="Reload cached data"
            >
              <RefreshCw className="h-4 w-4" />
              Reload
            </button>
            <button
              onClick={refresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-primary-500 bg-primary-500 hover:bg-primary-600 text-white text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              title="Collect fresh compliance data from the tenant (up to 5 min)"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing…' : 'Refresh Now'}
            </button>
            <button
              onClick={generateReport}
              disabled={generatingReport || !data?.evaluation}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-accent text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              title="Open a printable HIPAA compliance report in a new tab. Use your browser's print dialog to save as PDF."
            >
              <FileDown className={`h-4 w-4 ${generatingReport ? 'animate-pulse' : ''}`} />
              {generatingReport ? 'Generating…' : 'Generate HIPAA Report'}
            </button>
          </div>
        </div>
        {refreshMessage && (
          <div className="mt-3 text-sm text-muted-foreground italic">{refreshMessage}</div>
        )}
        {reportError && (
          <div className="mt-3 text-sm text-red-600 dark:text-red-400">{reportError}</div>
        )}
      </div>

      {/* Stub advisory */}
      {data.isExoStubbed && (
        <div className="border border-yellow-300 dark:border-yellow-700 rounded-md bg-yellow-50 dark:bg-yellow-900/20 p-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-medium text-yellow-800 dark:text-yellow-200">Exchange data unavailable in this snapshot</div>
            <div className="mt-1 text-yellow-700 dark:text-yellow-300">
              This snapshot was collected before the Exchange/Purview data path went live. Findings for DLP, retention,
              message encryption, and mail flow rules may not reflect the tenant's real state until the compliance
              runbook is validated (Phase 2c). Findings for MFA, Security Defaults, and licensing are live.
            </div>
          </div>
        </div>
      )}

      {/* No posture yet */}
      {!evaluation && (
        <div className="p-6 rounded-lg border border-border bg-card text-center">
          <Clock className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <div className="text-lg font-medium text-foreground">No compliance snapshot yet</div>
          <div className="text-sm text-muted-foreground mt-1">
            Run the daily sync for this client to collect a posture snapshot. This page will populate
            automatically on the next sync.
          </div>
        </div>
      )}

      {/* Score + stats */}
      {evaluation && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="border border-border rounded-lg bg-card p-4 flex flex-col items-center justify-center">
            <ScoreBadge score={evaluation.score} />
            <div className="mt-3 text-sm text-muted-foreground text-center">
              {evaluation.passedRuleIds.length} of {evaluation.passedRuleIds.length + evaluation.gaps.length} rules pass
            </div>
          </div>
          <div className="border border-border rounded-lg bg-card p-4 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Must fix</div>
            <div className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">{gapCounts['must-fix']}</div>
            <div className="text-xs text-muted-foreground mt-2">Blocking HIPAA compliance</div>
          </div>
          <div className="border border-border rounded-lg bg-card p-4 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Should fix</div>
            <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">{gapCounts['should-fix']}</div>
            <div className="text-xs text-muted-foreground mt-2">Auditor will flag</div>
          </div>
          <div className="border border-border rounded-lg bg-card p-4 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Snapshot</div>
            <div className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${staleness.color} w-fit`}>
              {staleness.label}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              Last synced {formatTimestamp(data.lastSynced)}
            </div>
          </div>
        </div>
      )}

      {/* Gaps list */}
      {evaluation && sortedGaps.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-3">Findings ({sortedGaps.length})</h2>
          <div className="space-y-2">
            {sortedGaps.map(g => <GapCard key={g.id} gap={g} />)}
          </div>
        </div>
      )}

      {/* Passed rules — collapsed */}
      {evaluation && evaluation.passedRuleIds.length > 0 && (
        <div>
          <button
            onClick={() => setShowPassed(!showPassed)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {showPassed ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <ShieldCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
            {evaluation.passedRuleIds.length} rules passing
          </button>
          {showPassed && (
            <div className="mt-3 flex flex-wrap gap-2">
              {evaluation.passedRuleIds.map(id => (
                <div key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-card text-xs">
                  <CheckCircle2 className="h-3 w-3 text-green-600 dark:text-green-400" />
                  <span className="font-mono">{id}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
